import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import CardIcon from "@material-ui/icons/CreditCardOutlined";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useConfirm } from "../../components/ConfirmDialog";

const useStyles = makeStyles(theme => ({
  page: { padding: 24, background: theme.app.canvas || theme.app.bg, minHeight: "100%" },
  wrap: { maxWidth: 880, margin: "0 auto" },
  h1: { fontFamily: theme.app.heading, fontSize: 22, fontWeight: 700, color: theme.app.ink, marginBottom: 18 },
  card: {
    background: "#fff", border: `1px solid ${theme.app.border}`, borderRadius: 14,
    padding: 18, marginBottom: 16,
  },
  cardTitle: { fontSize: 14, fontWeight: 600, color: theme.app.ink, marginBottom: 12 },
  row: { display: "flex", alignItems: "center", gap: 12, padding: "9px 0", borderTop: `1px solid ${theme.app.lineSoft}` },
  rowFirst: { borderTop: "none" },
  grow: { flex: 1, minWidth: 0 },
  muted: { fontSize: 12.5, color: theme.app.textSecondary },
  strong: { fontSize: 13.5, fontWeight: 600, color: theme.app.ink },
  pill: { fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6, textTransform: "uppercase", letterSpacing: ".03em" },
  ok: { color: "#1E7E34", background: "#E8F5EC" },
  warn: { color: "#9A6410", background: "#FBF1DE" },
  bad: { color: "#B12A2A", background: "#FBE9E9" },
  empty: { fontSize: 13, color: theme.app.textTertiary, padding: "10px 0" },
  mono: { fontFamily: theme.app.mono, fontSize: 11, wordBreak: "break-all" },
}));

const brl = c => `R$ ${((c || 0) / 100).toFixed(2).replace(".", ",")}`;
const date = d => (d ? new Date(d).toLocaleDateString("pt-BR") : "—");

// rótulo + tom por status. O cliente não deve precisar decifrar "past_due".
const STATUS = {
  paid: { label: "Paga", tone: "ok" },
  pending: { label: "Aguardando", tone: "warn" },
  failed: { label: "Não paga", tone: "bad" },
  expired: { label: "Expirada", tone: "bad" },
  refunded: { label: "Estornada", tone: "warn" },
  canceled: { label: "Cancelada", tone: "warn" },
};
const SUB_STATUS = {
  active: { label: "Ativa", tone: "ok" },
  pending: { label: "Aguardando pagamento", tone: "warn" },
  past_due: { label: "Pagamento em atraso", tone: "bad" },
  canceled: { label: "Encerrada", tone: "bad" },
};

const Billing = () => {
  const classes = useStyles();
  const { confirm } = useConfirm();
  const [plan, setPlan] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [cards, setCards] = useState([]);
  const [paying, setPaying] = useState(null);

  const load = useCallback(async () => {
    try {
      const [me, inv, pm] = await Promise.all([
        api.get("/me/plan").catch(() => ({ data: null })),
        api.get("/me/invoices").catch(() => ({ data: { invoices: [] } })),
        api.get("/me/payment-methods").catch(() => ({ data: { cards: [] } })),
      ]);
      setPlan(me.data);
      setInvoices((inv.data && inv.data.invoices) || []);
      setCards((pm.data && pm.data.cards) || []);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cancelSubscription = async () => {
    const ok = await confirm({
      title: "Cancelar assinatura",
      message:
        "Você continua com o plano até o fim do período já pago. Depois disso a conta volta para o plano Grátis — nada é apagado.",
      confirmText: "Cancelar assinatura",
      danger: true,
    });
    if (!ok) return;
    try {
      const { data } = await api.post("/me/subscription/cancel");
      toast.success(`Assinatura encerrada em ${date(data.until)}.`);
      load();
    } catch (err) {
      toastError(err);
    }
  };

  // troca agendada para a renovação (downgrade). Dá para desfazer enquanto o
  // ciclo não virou — o cliente pediu por engano com frequência suficiente.
  const undoPendingPlan = async () => {
    try {
      await api.delete("/me/subscription/pending-plan");
      toast.success("Troca de plano cancelada.");
      load();
    } catch (err) {
      toastError(err);
    }
  };

  // caminho de volta: quitar uma fatura recusada/vencida reativa a assinatura
  // (inclusive depois de a conta ter sido rebaixada para o Grátis).
  const payInvoice = async (inv, method) => {
    setPaying(inv.id);
    try {
      const { data } = await api.post(`/me/invoices/${inv.id}/pay`, { method });
      if (data.invoice.status === "paid") toast.success("Pagamento confirmado!");
      else if (data.invoice.pixQrCode) toast.success("Código PIX gerado — copie abaixo para pagar.");
      else toast.error(data.invoice.lastError || "A cobrança não foi aprovada.");
      load();
    } catch (err) {
      toastError(err);
    } finally {
      setPaying(null);
    }
  };

  const removeCard = async c => {
    const ok = await confirm({
      title: "Remover cartão",
      message: `Remover o cartão ${c.brand} •••• ${c.last4}?`,
      confirmText: "Remover",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/me/payment-methods/${c.id}`);
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const sub = plan && plan.subscription;
  const st = sub && (SUB_STATUS[sub.status] || { label: sub.status, tone: "warn" });

  return (
    <div className={classes.page}>
      <div className={classes.wrap}>
        <div className={classes.h1}>Assinatura e faturas</div>

        <div className={classes.card}>
          <div className={classes.cardTitle}>Plano atual</div>
          <div className={`${classes.row} ${classes.rowFirst}`}>
            <div className={classes.grow}>
              <div className={classes.strong}>{(plan && plan.plan && plan.plan.name) || "Grátis"}</div>
              <div className={classes.muted}>
                {plan && plan.cycle && plan.cycle.end
                  ? `Renova em ${date(plan.cycle.end)}`
                  : "Sem renovação automática"}
              </div>
            </div>
            {st && <span className={`${classes.pill} ${classes[st.tone]}`}>{st.label}</span>}
            {sub && sub.status !== "canceled" && (
              <Button size="small" onClick={cancelSubscription}>Cancelar</Button>
            )}
          </div>
          {sub && sub.pendingPlan && (
            <div className={classes.row}>
              <div className={classes.grow}>
                <div className={classes.strong}>Troca agendada</div>
                <div className={classes.muted}>
                  Você passa para o plano {sub.pendingPlan.name} em {date(sub.currentPeriodEnd)}.
                </div>
              </div>
              <Button size="small" onClick={undoPendingPlan}>Desfazer</Button>
            </div>
          )}
          {sub && sub.cancelAtPeriodEnd && !sub.pendingPlan && (
            <div className={classes.row}>
              <div className={classes.muted}>
                Assinatura encerra em {date(sub.currentPeriodEnd)}; depois a conta volta para o Grátis.
              </div>
            </div>
          )}
        </div>

        <div className={classes.card}>
          <div className={classes.cardTitle}>Formas de pagamento</div>
          {cards.length === 0 && <div className={classes.empty}>Nenhum cartão salvo.</div>}
          {cards.map((c, i) => (
            <div key={c.id} className={`${classes.row} ${i === 0 ? classes.rowFirst : ""}`}>
              <CardIcon style={{ color: "#929AA4" }} />
              <div className={classes.grow}>
                <div className={classes.strong}>{c.brand} •••• {c.last4}</div>
                <div className={classes.muted}>
                  {c.holderName || "—"}{c.isDefault ? " · padrão" : ""}
                </div>
              </div>
              <IconButton size="small" onClick={() => removeCard(c)}><DeleteIcon fontSize="small" /></IconButton>
            </div>
          ))}
        </div>

        <div className={classes.card}>
          <div className={classes.cardTitle}>Faturas</div>
          {invoices.length === 0 && <div className={classes.empty}>Nenhuma fatura ainda.</div>}
          {invoices.map((inv, i) => {
            const s = STATUS[inv.status] || { label: inv.status, tone: "warn" };
            return (
              <div key={inv.id} className={`${classes.row} ${i === 0 ? classes.rowFirst : ""}`}>
                <div className={classes.grow}>
                  <div className={classes.strong}>
                    {inv.planName || "Assinatura"} · {brl(inv.amountCents)}
                  </div>
                  <div className={classes.muted}>
                    {inv.method === "pix" ? "PIX" : `Cartão ${inv.cardBrand || ""} ${inv.cardLast4 ? `•••• ${inv.cardLast4}` : ""}`}
                    {" · "}
                    {inv.paidAt ? `paga em ${date(inv.paidAt)}` : `vence em ${date(inv.dueAt)}`}
                    {/* o motivo da recusa é o que evita o chamado de suporte */}
                    {inv.status === "failed" && inv.lastError ? ` · ${inv.lastError}` : ""}
                  </div>
                  {inv.status === "pending" && inv.pixQrCode && (
                    <div className={classes.mono} style={{ marginTop: 6 }}>
                      {inv.pixQrCode.slice(0, 60)}…{" "}
                      <Button
                        size="small"
                        onClick={() => { navigator.clipboard.writeText(inv.pixQrCode); toast.success("Código copiado"); }}
                      >
                        Copiar PIX
                      </Button>
                    </div>
                  )}
                </div>
                <span className={`${classes.pill} ${classes[s.tone]}`}>{s.label}</span>
                {/* em aberto: dá para tentar de novo sem refazer a contratação */}
                {(inv.status === "failed" || inv.status === "expired" ||
                  (inv.status === "pending" && !inv.pixQrCode)) && (
                  <>
                    {cards.length > 0 && (
                      <Button size="small" disabled={paying === inv.id} onClick={() => payInvoice(inv, "card")}>
                        Pagar no cartão
                      </Button>
                    )}
                    <Button size="small" disabled={paying === inv.id} onClick={() => payInvoice(inv, "pix")}>
                      Pagar com PIX
                    </Button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Billing;
